import { formatDating } from '../utils/dating';
import { useEffect, useMemo, useState } from 'react';
import { Download, Link2, Maximize2, Plus, RotateCcw, Save, Trash2, Upload } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { usePhotoMetadata } from '../hooks/usePhotoMetadata';
import { useLanguage } from '../hooks/useLanguage';
import SearchableSelect from '../components/SearchableSelect';
import { localiseCategoryName, localiseDetailTypeName } from '../i18n/filterTranslations';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const copy = {
  RU: {
    kind: 'ФОТОАРХИВ', title: 'Фотография', loading: 'Загрузка фотографии…', loadError: 'Не удалось загрузить фотографию.',
    enlarge: 'Увеличить', description: 'Описание', noDescription: 'Описание не добавлено.', saveDescription: 'Сохранить описание', reset: 'Сбросить', saving: 'Сохраняю…',
    technical: 'Технические сведения', uploaded: 'Загружено', fileType: 'Тип файла', fileSize: 'Размер файла', resolution: 'Разрешение', download: 'Скачать оригинал',
    order: 'Порядок показа', orderHelp: 'Чем выше значение, тем раньше фотография появляется в списках.', save: 'Сохранить', replace: 'Заменить файл', replaceHelp: 'Связи с записями сохранятся.', replacing: 'Заменяю…', replaceConfirm: 'Заменить файл фотографии? Связи сохранятся.',
    products: 'Изделия на фотографии', details: 'Детали на фотографии', noProducts: 'Связанных изделий нет.', noDetails: 'Связанных деталей нет.',
    createProduct: 'Создать изделие', attachProduct: 'Привязать изделие', createDetail: 'Создать деталь', attachDetail: 'Привязать деталь', basedOn: 'На основе этой фотографии', existingId: 'Указать ID существующей записи',
    year: 'Год', number: 'Номер', maker: 'Производитель', category: 'Назначение', type: 'Тип', delete: 'Удалить фотографию', deleteConfirm: 'Удалить фотографию без возможности восстановления?',
    productPrompt: 'Введите ID изделия', detailPrompt: 'Введите ID детали', attached: 'Связь добавлена.', attachError: 'Не удалось добавить связь.', saveError: 'Не удалось сохранить изменения.', replaceError: 'Не удалось заменить файл.', deleteError: 'Не удалось удалить фотографию.',
    chooseDetailType: 'Выберите тип детали', loadingTypes: 'Загрузка типов деталей…', typesError: 'Не удалось загрузить типы деталей.', noTypes: 'Нет доступных типов деталей.', retry: 'Повторить', cancel: 'Отмена', creating: 'Создаю…', createDetailError: 'Не удалось создать деталь. Попробуйте ещё раз.',
  },
  EN: {
    kind: 'PHOTO ARCHIVE', title: 'Photograph', loading: 'Loading photograph…', loadError: 'Unable to load the photograph.',
    enlarge: 'Enlarge', description: 'Description', noDescription: 'No description has been added.', saveDescription: 'Save description', reset: 'Reset', saving: 'Saving…',
    technical: 'Technical details', uploaded: 'Uploaded', fileType: 'File type', fileSize: 'File size', resolution: 'Resolution', download: 'Download original',
    order: 'Display order', orderHelp: 'A higher value moves the photograph towards the front of lists.', save: 'Save', replace: 'Replace file', replaceHelp: 'Links to records will be preserved.', replacing: 'Replacing…', replaceConfirm: 'Replace this photograph file? Record links will be preserved.',
    products: 'Examples in this photograph', details: 'Parts in this photograph', noProducts: 'No linked examples.', noDetails: 'No linked parts.',
    createProduct: 'Create example', attachProduct: 'Link example', createDetail: 'Create part', attachDetail: 'Link part', basedOn: 'Based on this photograph', existingId: 'Enter an existing record ID',
    year: 'Year', number: 'Number', maker: 'Maker', category: 'Category', type: 'Type', delete: 'Delete photograph', deleteConfirm: 'Permanently delete this photograph?',
    productPrompt: 'Enter example ID', detailPrompt: 'Enter part ID', attached: 'Link added.', attachError: 'Unable to add the link.', saveError: 'Unable to save changes.', replaceError: 'Unable to replace the file.', deleteError: 'Unable to delete the photograph.',
    chooseDetailType: 'Choose a part type', loadingTypes: 'Loading part types…', typesError: 'Unable to load part types.', noTypes: 'No part types are available.', retry: 'Retry', cancel: 'Cancel', creating: 'Creating…', createDetailError: 'Unable to create the part. Please try again.',
  },
};

function hasEditorRole(user) {
  const roles = [user?.role, ...(user?.roles || []), ...(user?.authorities || [])]
    .map((role) => typeof role === 'string' ? role : role?.authority || role?.name);
  return roles.some((role) => ['ADMIN', 'MODERATOR', 'ROLE_ADMIN', 'ROLE_MODERATOR'].includes(role));
}

function prettyBytes(value) {
  if (value == null) return null;
  let size = Number(value);
  if (!Number.isFinite(size)) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return size.toFixed(size < 10 ? 2 : 1) + ' ' + units[unit];
}

export default function PhotoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const canEdit = hasEditorRole(currentUser);
  const [photo, setPhoto] = useState(null);
  useDocumentTitle(photo ? `Фотография № ${photo.id}` : null);
  const [status, setStatus] = useState('loading');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [weight, setWeight] = useState('0');
  const [busy, setBusy] = useState('');
  const [formError, setFormError] = useState('');
  const [showDetailCreator, setShowDetailCreator] = useState(false);

  const refresh = async () => {
    const { data } = await api.get('/photos/' + id);
    setPhoto(data);
    setDescription(String(data?.description || ''));
    setWeight(String(data?.weight ?? 0));
    return data;
  };

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setShowDetailCreator(false);
    api.get('/photos/' + id)
      .then(({ data }) => {
        if (!active) return;
        setPhoto(data);
        setDescription(String(data?.description || ''));
        setWeight(String(data?.weight ?? 0));
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => { active = false; };
  }, [id]);

  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
  const fullUrl = photo?.url?.startsWith('/') ? uploadHost + photo.url : photo?.url || '';
  const metadata = usePhotoMetadata(fullUrl) || {};
  const sizeText = prettyBytes(metadata.size ?? photo?.sizeBytes ?? photo?.fileSize) || (typeof metadata.size === 'string' ? metadata.size : null);
  const products = photo?.products || [];
  const details = photo?.details || [];

  const technicalFacts = useMemo(() => [
    photo?.uploadedAt && [labels.uploaded, new Date(photo.uploadedAt).toLocaleString(lang === 'EN' ? 'en-GB' : 'ru-RU')],
    metadata.type && [labels.fileType, String(metadata.type).toUpperCase()],
    sizeText && [labels.fileSize, sizeText],
    metadata.width && metadata.height && [labels.resolution, metadata.width + '×' + metadata.height],
  ].filter(Boolean), [photo?.uploadedAt, metadata.type, metadata.width, metadata.height, sizeText, labels, lang]);

  const downloadPhoto = async () => {
    const base = (photo?.description?.trim() || 'photo-' + photo?.id).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 80);
    try {
      const response = await fetch(fullUrl, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const extension = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = base + '.' + extension; document.body.appendChild(anchor); anchor.click(); anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      const anchor = document.createElement('a');
      anchor.href = fullUrl; anchor.download = base + '.jpg'; anchor.target = '_blank'; document.body.appendChild(anchor); anchor.click(); anchor.remove();
    }
  };

  const saveFields = async (payload, busyName) => {
    setBusy(busyName); setFormError('');
    try { await api.patch('/photos/' + photo.id, payload); await refresh(); }
    catch { setFormError(labels.saveError); }
    finally { setBusy(''); }
  };

  const replacePhoto = async (file) => {
    if (!file || !window.confirm(labels.replaceConfirm)) return;
    setBusy('replace'); setFormError('');
    const body = new FormData(); body.append('file', file);
    try { await api.post('/photos/' + photo.id + '/replace', body, { headers: { 'Content-Type': 'multipart/form-data' } }); await refresh(); }
    catch { setFormError(labels.replaceError); }
    finally { setBusy(''); }
  };

  const createProduct = async () => {
    try {
      const { data } = await api.post('/products', { photos: [{ id: photo.id }], name: lang === 'EN' ? 'New example' : 'Новое изделие' });
      navigate('/products/' + data.id + '/edit');
    } catch { setFormError(labels.saveError); }
  };

  const attachRecord = async (kind) => {
    const recordId = window.prompt(kind === 'product' ? labels.productPrompt : labels.detailPrompt);
    if (!recordId) return;
    try {
      await api.post('/photos/' + photo.id + '/annotate', null, { params: kind === 'product' ? { productId: recordId } : { detailId: recordId } });
      await refresh();
    } catch { window.alert(labels.attachError); }
  };

  const deletePhoto = async () => {
    if (!window.confirm(labels.deleteConfirm)) return;
    try { await api.delete('/photos/' + photo.id); navigate(-1); }
    catch { window.alert(labels.deleteError); }
  };

  if (status === 'loading') return <main className="record-state page-width">{labels.loading}</main>;
  if (status === 'error' || !photo) return <main className="record-state page-width">{labels.loadError}</main>;

  return (
    <main className="photo-page page-width">
      <header className="photo-header"><p className="section-kicker">{labels.kind}</p><h1>{labels.title} <span>№{photo.id}</span></h1></header>

      <div className="photo-layout">
        <section className="photo-stage">
          <button type="button" onClick={() => setLightboxOpen(true)} aria-label={labels.enlarge}>
            <img src={fullUrl} alt={photo.description || ''} />
            <span><Maximize2 size={16} />{labels.enlarge}</span>
          </button>
        </section>

        <aside className="photo-sidebar">
          <section>
            <h2>{labels.description}</h2>
            {canEdit ? (
              <><textarea rows={5} value={description} onChange={(event) => setDescription(event.target.value)} /><div className="photo-form-actions"><button type="button" className="primary-button" disabled={busy === 'description'} onClick={() => saveFields({ description }, 'description')}><Save size={15} />{busy === 'description' ? labels.saving : labels.saveDescription}</button><button type="button" className="icon-button" onClick={() => setDescription(String(photo.description || ''))} aria-label={labels.reset}><RotateCcw size={15} /></button></div></>
            ) : <p>{photo.description?.trim() || labels.noDescription}</p>}
          </section>

          <section>
            <h2>{labels.technical}</h2>
            <dl>{technicalFacts.map(([term, value]) => <div key={term}><dt>{term}</dt><dd>{value}</dd></div>)}</dl>
            <button type="button" className="text-button" onClick={downloadPhoto}><Download size={15} />{labels.download}</button>
          </section>

          {canEdit && (
            <section className="photo-admin-panel">
              <h2>{labels.order}</h2><p>{labels.orderHelp}</p>
              <div className="photo-weight"><input type="number" step="1" value={weight} onChange={(event) => setWeight(event.target.value)} /><button type="button" className="secondary-button" disabled={busy === 'weight'} onClick={() => saveFields({ weight: Math.trunc(Number(weight)) }, 'weight')}>{labels.save}</button></div>
              <label className="photo-file"><Upload size={16} /><span>{busy === 'replace' ? labels.replacing : labels.replace}<small>{labels.replaceHelp}</small></span><input type="file" accept="image/*" disabled={busy === 'replace'} onChange={(event) => { replacePhoto(event.target.files?.[0]); event.target.value = ''; }} /></label>
            </section>
          )}
          {formError && <p className="photo-form-error">{formError}</p>}
        </aside>
      </div>

      <RelatedRecords title={labels.products} empty={labels.noProducts} records={products} kind="product" labels={labels} canEdit={canEdit} onCreate={createProduct} onAttach={() => attachRecord('product')} />
      <RelatedRecords title={labels.details} empty={labels.noDetails} records={details} kind="detail" labels={labels} canEdit={canEdit} onCreate={() => setShowDetailCreator(true)} onAttach={() => attachRecord('detail')}>
        {canEdit && showDetailCreator && <DetailCreator photoId={photo.id} labels={labels} onCancel={() => setShowDetailCreator(false)} onCreated={(detail) => navigate('/details/' + detail.id + '/edit')} />}
      </RelatedRecords>

      {canEdit && <div className="photo-danger"><button type="button" className="text-button danger-action" onClick={deletePhoto}><Trash2 size={15} />{labels.delete}</button></div>}

      <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} slides={[{ src: fullUrl }]} plugins={[Zoom]} carousel={{ finite: true }} controller={{ closeOnBackdropClick: true }} render={{ buttonPrev: () => null, buttonNext: () => null }} />
    </main>
  );
}

function DetailCreator({ photoId, labels, onCancel, onCreated }) {
  const { lang } = useLanguage();
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState(null);
  const [status, setStatus] = useState('loading');
  const [retry, setRetry] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setStatus('loading');
    api.get('/detailTypes').then(({ data }) => {
      if (!active) return;
      setTypes(Array.isArray(data) ? data : []);
      setStatus('ready');
    }).catch(() => { if (active) setStatus('error'); });
    return () => { active = false; };
  }, [retry]);

  const selectedType = types.find((type) => type.id === typeId);
  const options = types.map((type) => ({
    id: type.id,
    name: [localiseDetailTypeName(type, lang), localiseCategoryName(type.belongsTo, lang)].filter(Boolean).join(' · '),
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (creating || status !== 'ready' || !selectedType) return;
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/details', {
        photos: [{ id: photoId }],
        type: { id: selectedType.id },
        belongsTo: selectedType.belongsTo || null,
      });
      if (!data?.id) throw new Error('Missing detail ID');
      onCreated(data);
    } catch {
      setError(labels.createDetailError);
    } finally {
      setCreating(false);
    }
  };

  return (
    <form className="photo-create-detail" aria-label={labels.createDetail} onSubmit={submit}>
      <fieldset disabled={creating}>
        <legend>{labels.createDetail}</legend>
        {status === 'loading' && <p role="status">{labels.loadingTypes}</p>}
        {status === 'error' && <><p role="alert">{labels.typesError}</p><button type="button" className="text-button" onClick={() => setRetry((value) => value + 1)}>{labels.retry}</button></>}
        {status === 'ready' && (types.length > 0
          ? <SearchableSelect options={options} value={typeId} onChange={setTypeId} placeholder={labels.chooseDetailType} />
          : <p role="status">{labels.noTypes}</p>)}
        {error && <p role="alert">{error}</p>}
        <div className="photo-form-actions">
          <button type="submit" className="primary-button" disabled={status !== 'ready' || !selectedType || creating}>{creating ? labels.creating : labels.createDetail}</button>
          <button type="button" className="secondary-button" onClick={onCancel}>{labels.cancel}</button>
        </div>
      </fieldset>
    </form>
  );
}

function RelatedRecords({ title, empty, records, kind, labels, canEdit, onCreate, onAttach, children }) {
  const { lang } = useLanguage();
  return (
    <section className="photo-related">
      <header><h2>{title}</h2><span>{String(records.length).padStart(2, '0')}</span></header>
      {records.length === 0 && !canEdit && <p className="record-empty">{empty}</p>}
      <div className="photo-relations">
        {records.map((record) => {
          const manufacturer = typeof record.manufacturer === 'string' ? record.manufacturer : record.manufacturer?.name;
          return <Link to={'/' + (kind === 'product' ? 'products' : 'details') + '/' + record.id} key={record.id}><span>{kind === 'product' ? labels.products : labels.details} · {String(record.id).padStart(5, '0')}</span><strong>{record.name || record.type?.name || '#' + record.id}</strong><small>{[formatDating(record, lang) && labels.year + ': ' + formatDating(record, lang), (record.number || record.serialNumber) && labels.number + ': ' + (record.number || record.serialNumber), manufacturer && labels.maker + ': ' + manufacturer].filter(Boolean).join(' · ')}</small></Link>;
        })}
        {canEdit && !children && <button type="button" onClick={onCreate}><Plus size={18} /><strong>{kind === 'product' ? labels.createProduct : labels.createDetail}</strong><small>{labels.basedOn}</small></button>}
        {canEdit && <button type="button" onClick={onAttach}><Link2 size={18} /><strong>{kind === 'product' ? labels.attachProduct : labels.attachDetail}</strong><small>{labels.existingId}</small></button>}
      </div>
      {children}
    </section>
  );
}
